import SelectorEngine from '../../../../utils/dom/selector-engine';
import EventHandler from '../../../../utils/dom/event-handler';
import FormComponent from '../../../../components/FormComponent';
import { InputTypes, validFormElementValue } from '../../../../utils/validateFormElement';

/**
 * ManualContactCard FormComponent
 * Renders a "Full name" + "Email address" pair of inputs with a "Save contact" button.
 * Once saved, swaps to a summary card (reusing the `lta-selected-contact-summary` styles)
 * showing the saved name/email with a button to remove the contact and start again.
 */
class ManualContactCard extends FormComponent {
	markup(value, data = {}, titleMarkup) {
		const idBase = `manual-contact-card-${data.id}`;
		const savedContact = data.savedContact;

		return `
			<div class="lta-manual-contact-card">
				<div class="lta-manual-contact-card__title">${titleMarkup}</div>
				${savedContact ? this.renderSavedMarkup(savedContact) : this.renderFormMarkup(idBase, data)}
			</div>
		`;
	}

	renderFormMarkup(idBase, data) {
		const fullNameId = `${idBase}-full-name`;
		const emailId = `${idBase}-email`;

		return `
			<div class="lta-text-input__wrapper js-manual-contact-card-fullname-wrapper u-mb-3">
				<label for="${fullNameId}" class="lta-text-input__label u-text-grey-lta">${data.fullNameLabel || 'Full name'}</label>
				<div class="lta-text-input--error"></div>
				<input
					type="text"
					class="lta-text-input u-w-100 js-manual-contact-card-fullname"
					id="${fullNameId}"
					value="${data.draftFullName || ''}"
				/>
			</div>
			<div class="lta-text-input__wrapper js-manual-contact-card-email-wrapper u-mb-3">
				<label for="${emailId}" class="lta-text-input__label u-text-grey-lta">${data.emailLabel || 'Email address'}</label>
				<div class="lta-text-input--error"></div>
				<input
					type="email"
					class="lta-text-input u-w-100 js-manual-contact-card-email"
					id="${emailId}"
					value="${data.draftEmail || ''}"
				/>
			</div>
			<button type="button" class="lta-btn lta-btn--md lta-btn--line lta-btn--block js-manual-contact-card-save">
				${data.saveButtonLabel || 'Save contact'}
			</button>
		`;
	}

	renderSavedMarkup(contact) {
		return `
			<div class="lta-selected-contact-summary">
				<div class="lta-selected-contact-summary__content">
					<div class="lta-selected-contact-summary__content-name u-h6">${contact.fullName}</div>
					<div class="lta-selected-contact-summary__email u-body2 u-mt-1">${contact.email}</div>
				</div>
				<button type="button" class="lta-btn lta-btn--xs lta-btn--petite lta-btn--icon lta-btn--icon-before js-manual-contact-card-remove u-mt-2">
					<svg class="lta-icon js-lta-text-input--btn-icon u-mr-0" width="1.5em" height="1.5em" fill="currentColor" alt="search" aria-hidden="true">
						<use xlink:href="/static/images/icons/lta-icons.svg#close"></use>
					</svg>${this.data?.removeButtonLabel || 'Remove contact'}
				</button>
			</div>
		`;
	}

	afterRender() {
		this.rootEl = SelectorEngine.findOne('.lta-manual-contact-card', this.containerEl);
		this.fullNameWrapper = SelectorEngine.findOne('.js-manual-contact-card-fullname-wrapper', this.rootEl);
		this.emailWrapper = SelectorEngine.findOne('.js-manual-contact-card-email-wrapper', this.rootEl);
		this.fullNameInput = SelectorEngine.findOne('.js-manual-contact-card-fullname', this.rootEl);
		this.emailInput = SelectorEngine.findOne('.js-manual-contact-card-email', this.rootEl);

		const saveButton = SelectorEngine.findOne('.js-manual-contact-card-save', this.rootEl);
		if (saveButton) {
			EventHandler.on(saveButton, 'click', () => this.handleSaveContact());
		}

		const removeButton = SelectorEngine.findOne('.js-manual-contact-card-remove', this.rootEl);
		if (removeButton) {
			EventHandler.on(removeButton, 'click', () => this.handleRemoveContact());
		}

		// If a contact was already saved (e.g. pre-filled in edit mode), emit it once on
		// first render so the form can be submitted straight away without requiring the
		// user to click "Save contact"/"Remove" first.
		if (!this._hasEmittedInitialValue) {
			this._hasEmittedInitialValue = true;
			if (this.data?.savedContact) {
				this.emitValue(this.data.savedContact);
			}
		}
	}

	handleSaveContact() {
		const fullName = this.fullNameInput?.value?.trim();
		const email = this.emailInput?.value?.trim();

		// This field is optional (e.g. Free Park Tennis Administrator), so saving with both
		// fields left blank is treated the same as removing the contact - no admin set -
		// rather than being blocked by required field validation.
		if (!fullName && !email && this.data?.isOptional) {
			this.handleRemoveContact();
			return;
		}

		const isFullNameValid = validFormElementValue(fullName, {
			element: this.fullNameWrapper,
			errorMessage: this.data?.fullNameErrorMessage || 'Please enter a full name',
			inputType: InputTypes.SELECTOR_INPUT
		});

		const isEmailValid = validFormElementValue(email, {
			element: this.emailWrapper,
			type: 'email',
			errorMessage: this.data?.emailErrorMessage || 'Please enter a valid email address',
			inputType: InputTypes.SELECTOR_INPUT
		});

		if (!isFullNameValid || !isEmailValid) {
			return;
		}

		const savedContact = { fullName, email };
		this.updateData({ savedContact, draftFullName: '', draftEmail: '' });
		this.emitValue(savedContact);
		this.data?.onContactChange?.(savedContact);
	}

	handleRemoveContact() {
		this.updateData({ savedContact: null, draftFullName: '', draftEmail: '' });
		this.emitValue(null);
		this.data?.onContactChange?.(null);
	}

	emitValue(contact) {
		this.value = {
			fieldName: this.data.fieldName,
			value: contact,
			savedContact: contact
		};
	}

	validate() {
		if (this.data?.savedContact || this.data?.isOptional) {
			return true;
		}

		return validFormElementValue(null, {
			element: this.fullNameWrapper,
			errorMessage: this.data?.errorMessage || 'Please save your contact details before continuing',
			inputType: InputTypes.SELECTOR_INPUT
		});
	}
}

export default ManualContactCard;
